/* eslint-disable no-shadow */
/* eslint-disable import/no-cycle */

import ApiService from '@/services/api.service';
import { apiEndpoint } from '@/shared';
import { defaultSettingColors } from '@/shared/defaultColors';
import { HttpError, PMSError, ValidationError } from '@/errors';
import Vue from 'vue';
import translationModule from '@/store/translation.module';

const endpoint = `${apiEndpoint}/connectivity/booking-engine`;
const propertyEndpoint = `${apiEndpoint}/property`;

const state = {
  cultBookingLanguages: [],
  priceComparison: [],
  guestEmailMessages: [],
  cancellationEmailMessages: [],
  fullyBookedMessages: [],
  packagesPromotions: [],
  creditCardMessages: [],
  currencies: null,
  colors: null,
  additionalFeatures: null,
  numberFormat: null,
  updatePending: false,
  updateError: null,
  termsAndConditions: [],
  paymentGatewayKeys: [],
  availableNumberFormats: [],
  intlNumberFormats: [],
};

const getters = {
  cultBookingLanguages: (state) => (state.cultBookingLanguages != null ? state.cultBookingLanguages : []),
  priceComparison: (state) => (state.priceComparison != null ? state.priceComparison : []),
  currencies: (state) => (state.currencies != null ? state.currencies : []),
  intlNumberFormats: (state) => (state.intlNumberFormats != null ? state.intlNumberFormats : []),
  colors: (state) => (state.colors != null ? state.colors : []),
  additionalFeatures: (state) => (state.additionalFeatures != null ? state.additionalFeatures : []),
  numberFormat: (state) => (state.numberFormat != null ? state.numberFormat : []),
  fullyBookedMessages: (state) => state.fullyBookedMessages,
  packagesPromotions: (state) => state.packagesPromotions,
  termsAndConditions: (state) => state.termsAndConditions,
  creditCardMessages: (state) => state.creditCardMessages,
  paymentGatewayKeys: (state) => state.paymentGatewayKeys,
  guestEmailMessages: (state) => state.guestEmailMessages,
  cancellationEmailMessages: (state) => state.cancellationEmailMessages,
  availableNumberFormats: (state) => state.availableNumberFormats,
};

const mutations = {
  clearErrors() {
    // do nothing
  },
  beforeUpdate(state) {
    state.updateError = null;
    state.updatePending = true;
  },
  afterUpdate(state, error = null) {
    state.updatePending = false;
    state.updateError = error;
  },
  updateCultBookingLanguages(state, cultBookingLanguages) {
    state.cultBookingLanguages = cultBookingLanguages;
    state.updatePending = false;
  },
  setPriceComparison(state, data) {
    const priceComparison = data;
    const messages = [];
    translationModule.state.translationLanguageiso.forEach((l) => {
      const item = data.messages.find(({ languageCode }) => languageCode === l);
      messages.push({
        languageCode: l,
        heading: item ? item.heading : '',
        additionalText: item ? item.additionalText : '',
        protected: item ? item.protected : '',
      });
    });
    priceComparison.messages = [...messages];
    Vue.set(state, 'priceComparison', priceComparison);
  },
  setTermsAndConditions(state, data) {
    const termsAndConditions = [];
    translationModule.state.translationLanguageiso.forEach((l) => {
      const item = data.messages.find(({ languageCode }) => languageCode === l);
      termsAndConditions.push({
        languageCode: l,
        message: item ? item.message : '',
        url: item ? item.url : '',
        protected: item ? item.protected : '',
      });
    });
    Vue.set(state, 'termsAndConditions', [...termsAndConditions]);
    Vue.set(state.termsAndConditions, 'redirectUrl', data?.redirectUrl);
  },
  setTabMessageData(state, { tab, data }) {
    const tabData = [];
    translationModule.state.translationLanguageiso.forEach((l) => {
      const item = data.messages.find(({ languageCode }) => languageCode === l);
      tabData.push({
        languageCode: l,
        message: item ? item.message : '',
        protected: item ? item.protected : '',
      });
    });
    Vue.set(state, tab, [...tabData]);
  },
  setGuestEmailMessages(state, data) {
    const guestEmailMessages = [];
    translationModule.state.translationLanguageiso.forEach((l) => {
      const item = data.messages.find(({ languageCode }) => languageCode === l);
      guestEmailMessages.push({
        languageCode: l,
        body: item ? item.body : '',
        introduction: item ? item.introduction : '',
        signature: item ? item.signature : '',
        protected: item ? item.protected : '',
      });
    });
    Vue.set(state, 'guestEmailMessages', [...guestEmailMessages]);
  },
  currencies(state, currencies = []) {
    state.currencies = currencies;
  },
  setCancellationEmailMessages(state, data) {
    const cancellationEmailMessages = [];
    translationModule.state.translationLanguageiso.forEach((l) => {
      const item = data.messages.find(({ languageCode }) => languageCode === l);
      cancellationEmailMessages.push({
        languageCode: l,
        body: item ? item.body : '',
        introduction: item ? item.introduction : '',
        signature: item ? item.signature : '',
        protected: item ? item.protected : '',
      });
    });
    Vue.set(state, 'cancellationEmailMessages', [...cancellationEmailMessages]);
  },
  updateAdditionalFeatures(state, data) {
    state.additionalFeatures = data;
    if (typeof data.currencySymbol === 'undefined') {
      state.additionalFeatures.currencySymbol = true;
    }
    state.updatePending = false;
  },
  updateBookingEngineLogoData(state, data) {
    Vue.set(state.additionalFeatures, 'bookingEngineMarketingUrl', data?.bookingEngineMarketingUrl);
    Vue.set(state.additionalFeatures, 'bookingEngineLogoUrl', data?.bookingEngineLogoUrl);
    state.updatePending = false;
  },
  updateNumberFormat(state, data) {
    state.numberFormat = data;
    state.updatePending = false;
  },
  updateColor(state, data) {
    state.colors = {
      mainBackgroundColorType: data.mainBackgroundColorType ?? defaultSettingColors.mainBackgroundColorType,
      headerBackgroundColorType: data.headerBackgroundColorType ?? defaultSettingColors.headerBackgroundColorType,
      bookNowButtonColorType: data.bookNowButtonColorType ?? defaultSettingColors.bookNowButtonColorType,
      roomTypeBackgroundColorType: data.roomTypeBackgroundColorType
        ?? defaultSettingColors.roomTypeBackgroundColorType,
      mainBackgroundColor: data.mainBackgroundColor ?? defaultSettingColors.mainBackgroundColor,
      template_background_colour_top: data.template_background_colour_top
        ?? defaultSettingColors.template_background_colour_top,
      template_background_colour_bottom: data.template_background_colour_bottom
        ?? defaultSettingColors.template_background_colour_bottom,
      headerBackgroundColor: data.headerBackgroundColor ?? defaultSettingColors.headerBackgroundColor,
      header_background_colour_top: data.header_background_colour_top
        ?? defaultSettingColors.header_background_colour_top,
      header_background_colour_bottom: data.header_background_colour_bottom
        ?? defaultSettingColors.header_background_colour_bottom,
      roomTypeBackgroundColor: data.roomTypeBackgroundColor ?? defaultSettingColors.roomTypeBackgroundColor,
      product_background_colour_top: data.product_background_colour_top
        ?? defaultSettingColors.product_background_colour_top,
      product_background_colour_bottom: data.product_background_colour_bottom
        ?? defaultSettingColors.product_background_colour_bottom,
      inputFieldsBackgroundColor: data.inputFieldsBackgroundColor ?? defaultSettingColors.inputFieldsBackgroundColor,
      searchButtonFontColor: data.searchButtonFontColor ?? defaultSettingColors.searchButtonFontColor,
      searchButtonNormalStateBackgroundColor: data.searchButtonNormalStateBackgroundColor
        ?? defaultSettingColors.searchButtonNormalStateBackgroundColor,
      searchButtonMouseHoverBackgroundColor: data.searchButtonMouseHoverBackgroundColor
        ?? defaultSettingColors.searchButtonMouseHoverBackgroundColor,
      bookNowButtonFontColor: data.bookNowButtonFontColor ?? defaultSettingColors.bookNowButtonFontColor,
      bookNowButtonNormalStateColor: data.bookNowButtonNormalStateColor
        ?? defaultSettingColors.bookNowButtonNormalStateColor,
      bookNowButtonMouseHoverColor: data.bookNowButtonMouseHoverColor
        ?? defaultSettingColors.bookNowButtonMouseHoverColor,
      other_buttons_background_colour_top: data.other_buttons_background_colour_top
        ?? defaultSettingColors.other_buttons_background_colour_top,
      other_buttons_background_colour_bottom: data.other_buttons_background_colour_bottom
        ?? defaultSettingColors.other_buttons_background_colour_bottom,
      other_buttons_background_hover_colour_top: data.other_buttons_background_hover_colour_top
        ?? defaultSettingColors.other_buttons_background_hover_colour_top,
      other_buttons_background_hover_colour_bottom: data.other_buttons_background_hover_colour_bottom
        ?? defaultSettingColors.other_buttons_background_hover_colour_bottom,
      languageSelectionColor: data.languageSelectionColor ?? defaultSettingColors.languageSelectionColor,
      date_background_colour: data.date_background_colour ?? defaultSettingColors.date_background_colour,
      date_colour: data.date_colour ?? defaultSettingColors.date_colour,
      roomTypeTitleColor: data.roomTypeTitleColor ?? defaultSettingColors.roomTypeTitleColor,
      mealsIncludedColor: data.mealsIncludedColor ?? defaultSettingColors.mealsIncludedColor,
      mealsExcludedColor: data.mealsExcludedColor ?? defaultSettingColors.mealsExcludedColor,
      personsInfoFontColor: data.personsInfoFontColor ?? defaultSettingColors.personsInfoFontColor,
      productPriceFontColor: data.productPriceFontColor ?? defaultSettingColors.productPriceFontColor,
      roomTypeTitleFontStyle: data.roomTypeTitleFontStyle ?? defaultSettingColors.roomTypeTitleFontStyle,
      personsInfoFontStyle: data.personsInfoFontStyle ?? defaultSettingColors.personsInfoFontStyle,
      productPriceFontStyle: data.productPriceFontStyle ?? defaultSettingColors.productPriceFontStyle,
      checkInOutInputColor: data.checkInOutInputColor ?? defaultSettingColors.checkInOutInputColor,
      title_bar_colour: data.title_bar_colour ?? defaultSettingColors.title_bar_colour,
      title_bar_background_colour: data.title_bar_background_colour ?? defaultSettingColors.title_bar_background_colour,
      inputFieldsFontColor: data.inputFieldsFontColor ?? defaultSettingColors.inputFieldsFontColor,
      inputFieldsErrorColor: data.inputFieldsErrorColor ?? defaultSettingColors.inputFieldsErrorColor,
      inputFieldsSuccessColor: data.inputFieldsSuccessColor ?? defaultSettingColors.inputFieldsSuccessColor,
      descriptionRoomRateFontColor: data.descriptionRoomRateFontColor
        ?? defaultSettingColors.descriptionRoomRateFontColor,
      showOnMapButtonColorType: data.showOnMapButtonColorType
        ?? defaultSettingColors.showOnMapButtonColorType,
      showOnMapButtonNormalStateColor: data.showOnMapButtonNormalStateColor
        ?? data.headerBackgroundColor ?? defaultSettingColors.headerBackgroundColor,
      showOnMapButtonMouseHoverColor: data.showOnMapButtonMouseHoverColor
        ?? data.headerBackgroundColor ?? defaultSettingColors.headerBackgroundColor,
      showOnMapButtonFontColor: data.showOnMapButtonFontColor
        ?? data.checkInOutInputColor ?? defaultSettingColors.checkInOutInputColor,
      infoBoxFontColor: data.infoBoxFontColor
        ?? defaultSettingColors.infoBoxFontColor,
      infoBoxBackgroundColor: data.infoBoxBackgroundColor
        ?? defaultSettingColors.infoBoxBackgroundColor,
      confirmationEmailTopLineColor: data.confirmationEmailTopLineColor
        ?? defaultSettingColors.confirmationEmailTopLineColor,
      confirmationEmailResponseButtonBackgroundColor: data.confirmationEmailResponseButtonBackgroundColor
        ?? defaultSettingColors.confirmationEmailResponseButtonBackgroundColor,
      confirmationEmailResponseButtonFontColor: data.confirmationEmailResponseButtonFontColor
        ?? defaultSettingColors.confirmationEmailResponseButtonFontColor,
      calendarCheckInOutFontColor: data.calendarCheckInOutFontColor
          ?? defaultSettingColors.calendarCheckInOutFontColor,
      calendarCheckInOutBackgroundColor: data.calendarCheckInOutBackgroundColor
          ?? defaultSettingColors.calendarCheckInOutBackgroundColor,
      calendarCheckInOutBetweenBackgroundColor: data.calendarCheckInOutBetweenBackgroundColor
          ?? defaultSettingColors.calendarCheckInOutBetweenBackgroundColor,
      calendarCheckInOutBetweenFontColor: data.calendarCheckInOutBetweenFontColor
          ?? defaultSettingColors.calendarCheckInOutBetweenFontColor,
      promotionTagBackgroundColor: data.promotionTagBackgroundColor
          ?? defaultSettingColors.promotionTagBackgroundColor,
      promotionTagBorderColor: data.promotionTagBorderColor
          ?? defaultSettingColors.promotionTagBorderColor,
      promotionTagFontColor: data.promotionTagFontColor
          ?? defaultSettingColors.promotionTagFontColor,
    };
    state.updatePending = false;
  },
  removeCurrency(state, currencyToRemove) {
    // eslint-disable-next-line no-plusplus
    for (let i = state.currencies.length - 1; i >= 0; i--) {
      if (state.currencies[i] === currencyToRemove) {
        state.currencies.splice(i, 1);
      }
    }
  },
  addCurrency(state, currencyToAdd) {
    if (!state.currencies.includes(currencyToAdd)) {
      state.currencies.push(currencyToAdd);
    }
  },
  setPaymentGatewayKeys(state, data) {
    state.paymentGatewayKeys = data;
  },
  updateAvailableNumberFormats(state, data) {
    state.availableNumberFormats = data;
    state.updatePending = false;
  },
  intlNumberFormats(state, data) {
    state.intlNumberFormats = data;
  },
};

function handleApiError(commit, error) {
  const { status, data } = error.response;
  let err = error;
  switch (status) {
    case 400: // BadRequest
      err = new HttpError(status, data.message);
      break;
    case 409: // PMSError
      err = new PMSError(status, data.message);
      break;
    case 422:
      err = new ValidationError(status, data.message, data.errors);
      break;
    case 500:
      err = new Error(data.message);
      break;
    default:
      break;
  }
  // commit('update', err);
  throw err;
}

const actions = {
  async fetchGuestEmail({ commit }) {
    try {
      const { data } = await ApiService.get(`
        ${endpoint}/guest-email`);
      commit('setGuestEmailMessages', data);
    } catch (error) {
      handleApiError(commit, error);
    }
  },
  async fetchCancellationEmail({ commit }) {
    try {
      const { data } = await ApiService.get(`
        ${endpoint}/cancellation-email`);
      commit('setCancellationEmailMessages', data);
    } catch (error) {
      handleApiError(commit, error);
    }
  },
  async fetchCreditCard({ commit }) {
    try {
      const { data } = await ApiService.get(`
        ${endpoint}/credit-card`);
      commit('setTabMessageData', { tab: 'creditCardMessages', data });
    } catch (error) {
      handleApiError(commit, error);
    }
  },
  async fetchFullyBooked({ commit }) {
    try {
      const { data } = await ApiService.get(`
        ${endpoint}/fully-booked`);
      commit('setTabMessageData', { tab: 'fullyBookedMessages', data });
    } catch (error) {
      handleApiError(commit, error);
    }
  },
  async fetchPackagesPromotions({ commit }) {
    try {
      const { data } = await ApiService.get(`
        ${endpoint}/packages-promotions`);
      commit('setTabMessageData', { tab: 'packagesPromotions', data });
    } catch (error) {
      handleApiError(commit, error);
    }
  },
  async fetchTermsAndConditions({ commit }) {
    try {
      const { data } = await ApiService.get(`
        ${endpoint}/terms-and-conditions`);
      commit('setTermsAndConditions', data);
    } catch (error) {
      handleApiError(commit, error);
    }
  },
  async fetchPriceComparison({ commit }) {
    try {
      const { data } = await ApiService.get(`
        ${endpoint}/price-comparison-widget`);
      commit('setPriceComparison', data);
    } catch (error) {
      handleApiError(commit, error);
    }
  },
  async fetchCurrencies({ commit, rootGetters }) {
    try {
      const response = await ApiService.get(`${endpoint}/currency/${rootGetters['user/propertyRoomDBID']}`);
      const currencies = response?.data?.data?.currencies;
      commit('currencies', currencies);
    } catch (error) {
      // do nothing
      handleApiError(commit, error);
    }
  },
  async fetchAdditionalFeature({ commit }) {
    commit('beforeUpdate');
    try {
      const { data } = await ApiService.get(`${endpoint}/additional-feature`);
      commit('updateAdditionalFeatures', data);
    } catch (error) {
      commit('afterUpdate');
      throw handleApiError(commit, error);
    }
  },
  async fetchAllSettings({ commit, rootGetters }) {
    try {
      const { data } = await ApiService.get(`${endpoint}/all-settings/${rootGetters['user/propertyRoomDBID']}`);
      commit('updateColor', data.colorCustomisation);
      commit('updateCultBookingLanguages', data.languages);
      commit('updateAdditionalFeatures', data.additionalFeatures);
      commit('updateNumberFormat', data.numberFormat);
      commit('updateAvailableNumberFormats', data.intlNumberFormat);
      commit('currencies', data.currency?.currencies);
    } catch (error) {
      handleApiError(commit, error);
    }
  },
  async saveCurrencies({ commit, state, rootGetters }) {
    try {
      await ApiService.post(`${endpoint}/currency/${rootGetters['user/propertyRoomDBID']}`, { currencies: state.currencies });
    } catch (error) {
      // do nothing
      handleApiError(commit, error);
    }
  },
  async saveAdditionalFeature({ commit }, payload) {
    commit('beforeUpdate');
    try {
      const { data } = await ApiService.post(`${endpoint}/additional-feature`, payload);
      commit('updateAdditionalFeatures', data);
    } catch (error) {
      commit('afterUpdate');
      throw handleApiError(commit, error);
    }
  },
  async saveBookingEngineUrl({ commit }, data) {
    commit('beforeUpdate');
    try {
      let payload;
      if (data.bookingEngineLogo == null || data.bookingEngineLogo.upload == null) {
        payload = { ...data };
      } else {
        payload = new FormData();
        payload.appendFromObject(data);
      }
      const response = await ApiService.post(`${endpoint}/additional-feature/booking-engine-logo`, payload);
      const result = response?.data?.result ?? null;
      commit('updateBookingEngineLogoData', result);
    } catch (error) {
      commit('afterUpdate');
      throw handleApiError(commit, error);
    }
  },
  async saveNumberFormat({ commit }, data) {
    commit('beforeUpdate');
    try {
      const response = await ApiService.post(`${endpoint}/numberFormat`, data);
      const result = response?.data ?? null;
      commit('updateNumberFormat', result);
    } catch (error) {
      commit('afterUpdate');
      throw handleApiError(commit, error);
    }
  },
  async saveColor({ commit, rootGetters }, data) {
    commit('beforeUpdate');
    try {
      const response = await ApiService.post(`${endpoint}/color-customisation/${rootGetters['user/propertyRoomDBID']}`, data);
      const result = response?.data?.data?.result ?? null;
      commit('updateColor', result);
    } catch (error) {
      commit('afterUpdate');
      throw handleApiError(commit, error);
    }
  },
  async getPropertyColors({ commit }, propertyId) {
    commit('beforeUpdate');
    try {
      const response = await ApiService.get(`${endpoint}/color-customisation/${propertyId}`);
      const result = response?.data?.data?.result ?? null;
      commit('updateColor', result);
    } catch (error) {
      commit('afterUpdate');
      throw handleApiError(commit, error);
    }
  },
  async createCultBookingLanguages({ commit }, data) {
    commit('beforeUpdate');
    try {
      const response = await ApiService.post(`${propertyEndpoint}/master-data/languages`, data);
      const languages = response?.data ?? null;
      commit('updateCultBookingLanguages', languages?.data);
      const { mainLanguage, supportedLanguages, autoTranslatedLanguages } = data;
      commit('user/setMainLanguage', { lang: mainLanguage, supportedLanguages, autoTranslatedLanguages }, { root: true });
    } catch (error) {
      commit('afterUpdate');
      throw handleApiError(commit, error);
    }
  },
  async updatePriceComparison({ commit }, payload) {
    commit('beforeUpdate');
    try {
      await ApiService.post(`${endpoint}/price-comparison-widget`, payload);
    } catch (error) {
      throw handleApiError(commit, error);
    } finally {
      commit('afterUpdate');
    }
  },
  async updateGuestEmail({ commit }, payload) {
    commit('beforeUpdate');
    try {
      await ApiService.post(`${endpoint}/guest-email`, payload);
    } catch (error) {
      throw handleApiError(commit, error);
    } finally {
      commit('afterUpdate');
    }
  },
  async updateCancellationEmail({ commit }, payload) {
    commit('beforeUpdate');
    try {
      await ApiService.post(`${endpoint}/cancellation-email`, payload);
    } catch (error) {
      throw handleApiError(commit, error);
    } finally {
      commit('afterUpdate');
    }
  },
  async updateTermsAndConditions({ commit }, payload) {
    commit('beforeUpdate');
    try {
      await ApiService.post(`${endpoint}/terms-and-conditions`, payload);
    } catch (error) {
      throw handleApiError(commit, error);
    } finally {
      commit('afterUpdate');
    }
  },
  async updatePackagesPromotions({ commit }, payload) {
    commit('beforeUpdate');
    try {
      await ApiService.post(`${endpoint}/packages-promotions`, payload);
    } catch (error) {
      throw handleApiError(commit, error);
    } finally {
      commit('afterUpdate');
    }
  },
  async updateFullyBooked({ commit }, payload) {
    commit('beforeUpdate');
    try {
      await ApiService.post(`${endpoint}/fully-booked`, payload);
    } catch (error) {
      throw handleApiError(commit, error);
    } finally {
      commit('afterUpdate');
    }
  },
  async updateCreditCard({ commit }, payload) {
    commit('beforeUpdate');
    try {
      await ApiService.post(`${endpoint}/credit-card`, payload);
    } catch (error) {
      throw handleApiError(commit, error);
    } finally {
      commit('afterUpdate');
    }
  },
  async processUploadTermsFile({ commit }, file) {
    commit('beforeUpdate');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await ApiService.post(`${endpoint}/terms-and-conditions/upload`, formData);
      return data;
    } catch (error) {
      throw handleApiError(commit, error);
    } finally {
      commit('afterUpdate');
    }
  },
  async fetchPaymentPaymentKeys({ commit }) {
    try {
      const { data } = await ApiService.get(`${endpoint}/payment-gateway`);
      commit('setPaymentGatewayKeys', data);
    } catch (error) {
      handleApiError(commit, error);
    }
  },
  async updatePaymentGatewayKeys({ commit }, payload) {
    commit('beforeUpdate');
    try {
      const { data } = await ApiService.post(`${endpoint}/payment-gateway`, payload);
      return data;
    } catch (error) {
      throw handleApiError(commit, error);
    } finally {
      commit('afterUpdate');
    }
  },
  async updateDomainUrl({ commit }, payload) {
    commit('beforeUpdate');
    try {
      const { data } = await ApiService.post(`${endpoint}/additional-feature/save-domain`, payload);
      commit('updateAdditionalFeatures', data);
    } catch (error) {
      throw handleApiError(commit, error);
    } finally {
      commit('afterUpdate');
    }
  },
  async updateCustomDomainEmail({ commit }, payload) {
    commit('beforeUpdate');
    try {
      const { data } = await ApiService.post(`${endpoint}/additional-feature/save-custom-domain-email`, payload);
      commit('updateAdditionalFeatures', data);
    } catch (error) {
      throw handleApiError(commit, error);
    } finally {
      commit('afterUpdate');
    }
  },
  async updateCustomDomainEmailAddress({ commit }, payload) {
    commit('beforeUpdate');
    try {
      const { data } = await ApiService.post(`${endpoint}/additional-feature/save-custom-domain-email-address`, payload);
      commit('updateAdditionalFeatures', data);
    } catch (error) {
      throw handleApiError(commit, error);
    } finally {
      commit('afterUpdate');
    }
  },
  async verifyDomainUrl({ commit }, payload) {
    commit('beforeUpdate');
    try {
      const { data } = await ApiService.post(`${endpoint}/additional-feature/verify-domain`, payload);
      commit('updateAdditionalFeatures', data);
    } catch (error) {
      throw handleApiError(commit, error);
    } finally {
      commit('afterUpdate');
    }
  },
  async getIntlNumberFormats({ commit }) {
    try {
      if (state.intlNumberFormats.length) return;
      const { data } = await ApiService.get(`${endpoint}/intl-number-formats`);
      commit('intlNumberFormats', data);
    } catch (error) {
      handleApiError(commit, error);
    }
  },
  async getLanguageSettings({ commit }, propertyId) {
    commit('beforeUpdate');
    try {
      const { data } = await ApiService.get(`${endpoint}/language/${propertyId}`);
      commit('updateCultBookingLanguages', data.data.result);
    } catch (error) {
      handleApiError(commit, error);
    } finally {
      commit('afterUpdate');
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
